import React, { useState, useEffect } from 'react';
import { SettingOutlined, DownOutlined, ExperimentOutlined, ThunderboltOutlined, InfoCircleOutlined, CheckCircleFilled } from '@ant-design/icons';
import { Select, Switch, message } from 'antd';

const specialAdCategories = [
  { value: 'NONE', label: 'None' },
  { value: 'CREDIT', label: 'Credit' },
  { value: 'HOUSING', label: 'Housing' },
  { value: 'EMPLOYMENT', label: 'Employment' },
  { value: 'POLITICAL_SOCIAL_ISSUES', label: 'Political / Social Issues' },
];

const MetaCampaignSettings = ({ campaignData, onUpdate, onNext }) => {
  const [campaignName, setCampaignName] = useState(campaignData.metaAds?.campaignName || '');
  const [buyingType, setBuyingType] = useState(campaignData.metaAds?.buyingType || 'AUCTION');
  const [abTestEnabled, setAbTestEnabled] = useState(campaignData.metaAds?.abTestEnabled || false);
  const [advantagePlusBudget, setAdvantagePlusBudget] = useState(campaignData.metaAds?.advantagePlusBudget || false);
  const [abTestVariable, setAbTestVariable] = useState(campaignData.metaAds?.abTestVariable || 'CREATIVE');
  const [abTestDuration, setAbTestDuration] = useState(campaignData.metaAds?.abTestDuration || '7');
  const [abTestWinnerBy, setAbTestWinnerBy] = useState(campaignData.metaAds?.abTestWinnerBy || 'COST_PER_RESULT');
  const [specialAdCategory, setSpecialAdCategory] = useState(campaignData.metaAds?.specialAdCategory || 'NONE');

  // Keep parent data in sync as fields change, so Review & Launch sees them even
  // when this step's Continue button isn't clicked (single-page campaign view)
  useEffect(() => {
    onUpdate({
      metaAds: {
        campaignName,
        buyingType,
        abTestEnabled,
        abTestVariable,
        abTestDuration,
        abTestWinnerBy,
        specialAdCategory,
      }
    });
  }, [campaignName, buyingType, abTestEnabled, abTestVariable, abTestDuration, abTestWinnerBy, specialAdCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdvantagePlusToggle = (checked) => {
    setAdvantagePlusBudget(checked);
    // Immediately sync to parent so NewCampaignUX can use it for step skipping
    onUpdate({
      metaAds: {
        ...campaignData.metaAds,
        advantagePlusBudget: checked,
      }
    });
  };

  const handleContinue = () => {
    if (!campaignName.trim()) {
      message.warning('Please enter a campaign name');
      return;
    }
    onUpdate({
      metaAds: {
        ...campaignData.metaAds,
        campaignName,
        buyingType,
        abTestEnabled,
        advantagePlusBudget,
        abTestVariable,
        abTestDuration,
        abTestWinnerBy,
        specialAdCategory,
      }
    });
    onNext();
  };

  return (
    <div className="step-container" style={{ maxWidth: '900px', margin: '0 auto', padding: '0 20px 100px' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px' }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '16px', background: '#f3f4f6',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px',
        }}>
          <SettingOutlined style={{ fontSize: '32px', color: '#111827' }} />
        </div>
        <h1 style={{ fontSize: '32px', fontWeight: 700, color: '#000', marginBottom: '12px', textAlign: 'center' }}>
          Campaign Settings
        </h1>
        <p style={{ fontSize: '16px', color: 'var(--ink-200)', marginBottom: 0, textAlign: 'center' }}>
          Configure your Meta campaign basics
        </p>
      </div>

      {/* Main Settings Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: '24px', 
        marginBottom: '24px' 
      }}>
        {/* Campaign Name */}
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            Campaign Name
          </label>
          <input
            type="text"
            placeholder="Enter campaign name..."
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            style={{
              width: '100%', padding: '12px 16px', fontSize: '15px',
              border: '1px solid var(--paper-300)', borderRadius: '8px', outline: 'none',
              transition: 'border-color 0.2s ease', boxSizing: 'border-box',
            }}
            onFocus={(e) => e.target.style.borderColor = 'var(--ink)'}
            onBlur={(e) => e.target.style.borderColor = 'var(--paper-300)'}
          />
        </div>

        {/* Buying Type */}
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            Buying Type
          </label>
          <Select
            value={buyingType}
            onChange={setBuyingType}
            style={{ width: '100%' }}
            size="large"
            options={[
              { value: 'AUCTION', label: 'Auction' },
              { value: 'RESERVATION', label: 'Reservation' },
            ]}
          />
          <div style={{ fontSize: '13px', color: 'var(--ink-200)', marginTop: '6px' }}>
            {buyingType === 'AUCTION'
              ? 'Bid competitively for ad placements (recommended)'
              : 'Reserve specific ad placements with fixed pricing'}
          </div>
        </div>

        {/* Special Ad Category */}
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            Special Ad Category
          </label>
          <div style={{ fontSize: '13px', color: 'var(--ink-200)', marginBottom: '8px' }}>
            Required for credit, housing, employment, or political ads
          </div>
          <Select
            value={specialAdCategory}
            onChange={setSpecialAdCategory}
            style={{ width: '100%' }}
            size="large"
            options={specialAdCategories}
          />
          {specialAdCategory !== 'NONE' && (
            <div style={{
              marginTop: '10px', padding: '10px 14px', background: '#fef3c7',
              border: '1px solid #fde68a', borderRadius: '8px',
              fontSize: '13px', color: '#92400e',
            }}>
              ⚠️ Selecting a special ad category limits targeting options and requires compliance with Meta's policies for this category.
            </div>
          )}
        </div>
      </div>

      {/* A/B Test and Advantage+ Sections - 2 Columns */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(2, 1fr)', 
        gap: '24px', 
        marginBottom: '24px' 
      }}>
        {/* A/B Test Section */}
        <div style={{ background: '#fff', border: '1px solid var(--paper-300)', borderRadius: '12px', padding: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: abTestEnabled ? '20px' : 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '8px', background: '#f3f4f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ExperimentOutlined style={{ fontSize: '20px', color: 'var(--ink-200)' }} />
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>A/B Test</div>
              <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
                Help improve ad performance by comparing versions to see what works best
              </div>
            </div>
          </div>
          <Switch
            checked={abTestEnabled}
            onChange={setAbTestEnabled}
            style={{ background: abTestEnabled ? 'var(--ink)' : 'var(--paper-300)' }}
          />
        </div>

        {abTestEnabled && (
          <div style={{ paddingTop: '20px', borderTop: '1px solid var(--paper-300)' }}>
            <div style={{ fontSize: '13px', color: 'var(--ink-200)', marginBottom: '16px', lineHeight: 1.5 }}>
              Test one variable at a time. Each version will be shown to separate audience groups for accuracy.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                  What do you want to test?
                </label>
                <Select
                  value={abTestVariable}
                  onChange={setAbTestVariable}
                  style={{ width: '100%' }}
                  size="large"
                  options={[
                    { value: 'CREATIVE', label: 'Creative' },
                    { value: 'AUDIENCE', label: 'Audience' },
                    { value: 'PLACEMENT', label: 'Placement' },
                    { value: 'DELIVERY_OPTIMIZATION', label: 'Delivery Optimization' },
                  ]}
                />
                <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginTop: '6px' }}>Compare different versions</div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                  Test duration
                </label>
                <Select
                  value={abTestDuration}
                  onChange={setAbTestDuration}
                  style={{ width: '100%' }}
                  size="large"
                  options={[
                    { value: '3', label: '3 days' },
                    { value: '7', label: '7 days (Recommended)' },
                    { value: '14', label: '14 days' },
                    { value: '30', label: '30 days' },
                  ]}
                />
                <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginTop: '6px' }}>How long to run the test</div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                  Determine winner by
                </label>
                <Select
                  value={abTestWinnerBy}
                  onChange={setAbTestWinnerBy}
                  style={{ width: '100%' }}
                  size="large"
                  options={[
                    { value: 'COST_PER_RESULT', label: 'Cost per result' },
                    { value: 'TOTAL_REACH', label: 'Total reach' },
                    { value: 'ENGAGEMENT', label: 'Engagement' },
                    { value: 'CONVERSIONS', label: 'Conversions' },
                  ]}
                />
                <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginTop: '6px' }}>Success metric</div>
              </div>
            </div>
          </div>
        )}
        </div>

        {/* Advantage+ Campaign Budget */}
        <div style={{
          background: '#fff',
          border: advantagePlusBudget ? '2px solid var(--ink)' : '1px solid var(--paper-300)',
          borderRadius: '12px',
          padding: '20px',
          transition: 'border 0.2s ease',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: advantagePlusBudget ? '16px' : 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '8px',
              background: advantagePlusBudget ? '#eff6ff' : '#f3f4f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ThunderboltOutlined style={{ fontSize: '20px', color: advantagePlusBudget ? 'var(--ink)' : 'var(--ink-200)' }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                  Advantage+ Campaign Budget
                </div>
                {advantagePlusBudget && (
                  <span style={{
                    fontSize: '11px', fontWeight: 600, color: 'var(--ink)',
                    background: '#eff6ff', padding: '2px 8px', borderRadius: '20px',
                  }}>
                    ENABLED
                  </span>
                )}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--ink-200)', marginTop: '2px' }}>
                Automatically optimize your budget distribution across ad sets
              </div>
            </div>
          </div>
          <Switch
            checked={advantagePlusBudget}
            onChange={handleAdvantagePlusToggle}
            style={{ background: advantagePlusBudget ? 'var(--ink)' : 'var(--paper-300)' }}
          />
        </div>

        {/* Expanded info when ON — exactly like Meta */}
        {advantagePlusBudget && (
          <div style={{
            borderTop: '1px solid var(--paper-300)',
            paddingTop: '16px',
          }}>
            {/* Blue info banner */}
            <div style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
            }}>
              <InfoCircleOutlined style={{ color: 'var(--ink)', fontSize: '16px', marginTop: '2px', flexShrink: 0 }} />
              <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: 1.6 }}>
                <strong>Budget is now set at campaign level.</strong> Meta will automatically distribute your budget across ad sets to maximize performance. You will not set individual ad set budgets.
              </div>
            </div>

            {/* What this means */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                'Budget is managed at the campaign level, not per ad set',
                'Meta automatically shifts spend to best-performing ad sets',
                'Ad set budget step will be skipped in this flow',
                'You can still set bid strategies per ad set',
              ].map((point, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <CheckCircleFilled style={{ color: 'var(--ink)', fontSize: '14px', marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: 'var(--ink-100)' }}>{point}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info when OFF */}
        {!advantagePlusBudget && (
          <div style={{ fontSize: '13px', color: 'var(--ink-200)', marginTop: '8px' }}>
            When disabled, you will set individual budgets for each ad set in the next steps.
          </div>
        )}
      </div>
      </div>

      {/* Special Ad Category */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
          Special Ad Category
        </label>
        <div style={{ fontSize: '13px', color: 'var(--ink-200)', marginBottom: '8px' }}>
          Required for credit, housing, employment, or political ads
        </div>
        <Select
          value={specialAdCategory}
          onChange={setSpecialAdCategory}
          style={{ width: '100%' }}
          size="large"
          options={specialAdCategories}
        />
        {specialAdCategory !== 'NONE' && (
          <div style={{
            marginTop: '10px', padding: '10px 14px', background: '#fef3c7',
            border: '1px solid #fde68a', borderRadius: '8px',
            fontSize: '13px', color: '#92400e',
          }}>
            ⚠️ Selecting a special ad category limits targeting options and requires compliance with Meta's policies for this category.
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff',
        borderTop: '1px solid var(--paper-300)', padding: '16px 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10,
      }}>
        <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
          Building: {campaignName || 'Untitled Campaign'}
        </div>
        <button
          onClick={handleContinue}
          style={{
            padding: '12px 32px',
            background: campaignName.trim() ? 'var(--ink)' : 'var(--paper-300)',
            color: '#fff', border: 'none', borderRadius: '8px',
            fontSize: '15px', fontWeight: 600,
            cursor: campaignName.trim() ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', gap: '8px',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => { if (campaignName.trim()) e.currentTarget.style.background = '#111827'; }}
          onMouseLeave={(e) => { if (campaignName.trim()) e.currentTarget.style.background = 'var(--ink)'; }}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default MetaCampaignSettings;